import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep, kebabCase } from 'lodash';
import { useFormContext } from "react-hook-form";
// Hooks
import useToggle from "hooks/useToggle";
import useMenu from "hooks/useMenu";
import { useQuizPageActions } from "hooks/useQuizPageActions";
// Context
import { useStream } from './StreamContext';
import { useCommitVersion } from './VersionHistoryContext';
// Redux
import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { QuizAgentActions } from "store/quizAgent/quizAgentSlice";
import { selectThreadId, selectLoading } from "store/quizAgent/quizAgentSelectors";
import { selectEventsFeatureEnable } from "store/currentUser/currentUserSelectors";
import QuizTypes from "types/QuizTypes";
import PageTypes from "types/PageTypes";
import QuestionTypes from "types/QuestionTypes";
import IPage from 'models/Page';
// Components
import { Textarea } from "components/Controls";
import { IconButton } from 'components/Buttons';
import QuizFrame from 'components/QuizFrame';
import MessagesList from "./MessagesList";
import QuizPageEditForm from "./QuizPageEdit.dialog";
import PromoVideoTile, { getAddButtonStyle } from "./PromoVideoTile";
import ConfirmationDialog from "dialogs/Confirmation.dialog";
import { Tooltip } from "components/Tooltip";
// Icons
import { FaArrowUp, FaEdit, FaPlus } from "react-icons/fa";
import { FiLayout } from "react-icons/fi";
import { MdClose, MdDeleteOutline } from "react-icons/md";
import { BsStars } from "react-icons/bs";
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { decodeBase64, quizTypesLabels } from "utilities/utilities";
import { formValuesToQuiz } from "utilities/quizFormMapping";
import { scoringQuizTypes, pollQuizTypes } from "utilities/quizTypeDefaults";
import { isQuizPageEditable, getQuizPageLabel } from "./pageEditRegistry";

interface IScreen {
  page: string;
  pageRef?: string;
  label: string;
}

// Fields rendered by every page (theme, logo).
const SHARED_VISUAL_FIELDS = [
  'theme', 'logoImage', 'logoFormat', 'logoImageBackgroundColor',
  'sponsorImage', 'sponsorImageFormat', 'sponsorImageBackgroundColor',
  'sponsorImagePages', 'backgroundImage'
];

// Fields that each static page exclusively cares about (on top of shared fields).
const STATIC_PAGE_FIELDS: Record<string, string[]> = {
  start:      ['landingPage', 'prizes', 'prizesButtonGradientStart', 'prizesButtonGradientEnd', 'prizesButtonFontColor',
              'spotPrizes', 'startsDate', 'endsDate', 'additionalLanguages'],
  waiver:     ['waiver'],
  countdown:  ['countdownPage'],
  register:   ['registrationPage', 'consents', 'consentsLinks'],
  'post-quiz': ['callToAction'],
  results:    ['resultsPage'],
  leaderboard: ['resultsPage'],
};

function pickFields(obj: any, keys: string[]): Record<string, any> {
  return Object.fromEntries(keys.filter(k => k in obj).map(k => [k, obj[k]]));
}

// Short "e.g." hints per page type, shown in the textarea placeholder once a
// screen is targeted — lets the user see what's typically worth asking for
// without opening the edit form first. Keyed the same way as STATIC_PAGE_FIELDS
// / pageEditRegistry (static page keys, or PageTypes for dynamic pages).
const PAGE_FIELD_HINTS: Record<string, string> = {
  start: 'background image, headline text, button text',
  waiver: 'waiver text, waiver image',
  register: 'registration message, date of birth / phone number fields',
  countdown: 'countdown text, countdown image',
  'post-quiz': 'title, url, button text, leaderboard button text, image',
  results: 'title, theme, social share',
  leaderboard: 'title, theme, social share',
  [PageTypes.Question]: 'question text, answer options, question image',
  [PageTypes.Banner]: 'banner text, banner image or video',
  [PageTypes.Timer]: 'timer duration, timer title',
  [PageTypes.Waiting]: 'waiting text, waiting image',
  [PageTypes.AdditionalQuestions]: 'additional questions, answers',
};

type Props = {
  quizHash: string;
  quizId?: string;
}

const QuizAgentChatScreen:FC<Props> = ({ quizHash, quizId }) => {
  const { t } = useTranslation();

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { startStream, startThread } = useStream();

  const commitVersion = useCommitVersion();

  const dispatch = useAppDispatch();

  const { getValues } = useFormContext<any>();

  const { deletePage, addPageAt } = useQuizPageActions();

  const threadId = useAppSelector(selectThreadId);
  const loading = useAppSelector(selectLoading);
  const eventsFeatureEnable = useAppSelector(selectEventsFeatureEnable);

  const [message, setMessage] = useState('');
  // Reference to the screen targeted via the per-screen "AI Edit" button,
  // shown as a removable chip above the textarea. Kept separate from
  // `message` so the prompt text stays freely editable and the final
  // instruction is only assembled on send. `page` is carried alongside the
  // human-readable `reference` so the textarea placeholder can show
  // per-page-type field hints without re-deriving them from the label.
  const [pageContext, setPageContext] = useState<{ reference: string; page: string } | null>(null);
  const [selectedPage, setSelectedPage] = useState<{ page: string; pageRef?: string; preAddSnapshot?: any } | null>(null);
  // UUID of the page queued for deletion. Drives the ConfirmationDialog: when
  // non-null the dialog is open, on confirm we actually call deletePage().
  const [pendingDeleteUuid, setPendingDeleteUuid] = useState<string | null>(null);
  // Index in pages[] where the next page will be inserted (set when opening the add-page menu).
  const [insertAtIndex, setInsertAtIndex] = useState<number>(0);
  // Which gap is hovered (index = position after screens[index]).
  const [hoveredGap, setHoveredGap] = useState<number | null>(null);

  const onChange = (e:React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const quiz = useMemo(() => (quizHash ? decodeBase64(quizHash) : {}), [quizHash]);

  const getPageHash = useCallback((screen: IScreen): string => {
    if (screen.pageRef) {
      // Dynamic page: IPage.refs are UUIDs — look up the actual data objects so
      // content edits (not just structural changes) are detected.
      const pg = quiz.pages?.find((p: IPage) => p.uuid === screen.pageRef);
      const refs: string[] = pg?.refs ?? [];
      const referencedData = refs.map((ref: string) => (
        quiz.questions?.find((q: any) => q.ref === ref)
        ?? quiz.banners?.find((b: any) => b.ref === ref)
        ?? quiz.timers?.find((t: any) => t.ref === ref)
        ?? quiz.waitings?.find((w: any) => w.ref === ref)
        ?? null
      ));
      return JSON.stringify({
        page: pg ?? null,
        data: referencedData,
        questionsSettings: quiz.questionsSettings,
        shared: pickFields(quiz, SHARED_VISUAL_FIELDS),
      });
    }

    // Static page: shared visual fields + this page's own fields only.
    // Nothing from questions/pages/banners/timers so edits there don't trigger
    // reloads of unrelated static screens.
    const specificFields = STATIC_PAGE_FIELDS[screen.page] ?? [];
    return JSON.stringify(pickFields(quiz, [...SHARED_VISUAL_FIELDS, ...specificFields]));
  }, [quiz]);

  const getPageIndex = useCallback((pageType: string, pageRef: string) => {
    if (pageType !== PageTypes.Question) return '';
    return quiz.pages?.filter((page:IPage) => page.type === pageType).findIndex((page:IPage) => page.uuid === pageRef) + 1;
  }, [quiz.pages]);

  // The agent knows about questions, not "pages" — a question can share a
  // page with banners/timers, so "Update Question Page 3" is a concept it
  // has to guess at. Referring to "the 1st question" instead maps directly
  // onto what it actually tracks, and reads better for the human sending it
  // too (this string is shown verbatim in the chip and the outgoing message).
  const ordinal = (n: number): string => {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  const getScreenReference = (screen: IScreen): string => {
    if (screen.page === PageTypes.Question && screen.pageRef) {
      const index = getPageIndex(PageTypes.Question, screen.pageRef);
      return `${ordinal(index as number)} question`;
    }
    return /page$/i.test(screen.label) ? screen.label : `${screen.label} page`;
  };

  // Note: the `editable` decision is derived later from the central
  // pageEditRegistry — keeping it out of this array means there's only one
  // place to flip a page on/off as forms become available.
  const screens:IScreen[] = useMemo(() => [
    // { page: 'promo' },
    { page: 'start', label: 'Landing Page' },
    { page: 'waiver', label: 'Waiver' },
    // { page: 'brand' },
    quiz.registrationPage?.showPriorQuiz && { page: 'register', label: 'Registration' },
    quiz.type !== QuizTypes.Survey && { page: 'countdown', label: 'Countdown' },
    ...( quiz.pages?.map((page:IPage) => ({ page: page.type, pageRef: page.uuid, label: `${page.type} ${getPageIndex(page.type, page.uuid)}` })) || [] ),
    !quiz.registrationPage?.showPriorQuiz && { page: 'register', label: 'Registration' },
    quiz.type !== QuizTypes.Personality && { page: 'post-quiz', label: 'Post Quiz' },
    pollQuizTypes.includes(quiz.type) && { page: 'results', label: 'Results' },
    scoringQuizTypes.includes(quiz.type) && { page: 'leaderboard', label: 'Leaderboard' },
  ].filter(Boolean), [quiz, getPageIndex]);

  const handleAiEdit = (screen: IScreen) => {
    setPageContext({ reference: getScreenReference(screen), page: screen.page });
    textAreaRef.current?.focus();
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    // Prepend the page-scoped instruction built from the chip, if one is set,
    // without ever mixing it into the freely-editable `message` text itself.
    // Blockquote-style prefix reads clearly to the agent and renders better
    // in the chat than a single run-on sentence.
    const fullMessage = pageContext
      ? `>> for the ${pageContext.reference}\n\n${message}`
      : message;

    dispatch(QuizAgentActions.addUserMessage(fullMessage));

    // Snapshot the current unified form (RHF) and convert to DB-shape so the
    // agent always receives the freshest draftQuiz, including any user edits
    // applied through controllers since the previous turn.
    const draftQuiz = formValuesToQuiz(getValues());

    let activeThreadId = threadId;
    if (!threadId) {
      const result = await startThread({ quizId, draftQuiz });
      activeThreadId = result.payload?.threadId;
    }

    if (!activeThreadId) return;

    const data = {
      threadId: activeThreadId,
      message: fullMessage,
      draftQuiz,
    };

    startStream(data);

    setMessage('');
    setPageContext(null);
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (loading) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  const { open, toggle } = useToggle();
  const { Menu: AddPageMenu, MenuItem: AddPageMenuItem, openMenu: openAddPageMenu } = useMenu();

  const handleOpenPageEdit = (page: string, pageRef?: string) => {
    setSelectedPage({ page, pageRef });
    toggle();
  }

  const handleClosePageEdit = () => {
    toggle();
    // Defer clearing so the dialog can animate out without flashing empty.
    setTimeout(() => setSelectedPage(null), 0);
  }

  // `gapAfterScreenIndex` is the index in `screens` after which the gap lives.
  // We compute how many dynamic pages (those with pageRef) appear up to and
  // including that screen — that gives us the insertion point in pages[].
  const handleOpenAddPageMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    gapAfterScreenIndex: number,
  ) => {
    const dynamicPagesBefore = screens
      .slice(0, gapAfterScreenIndex + 1)
      .filter((s) => !!s.pageRef).length;
    setInsertAtIndex(dynamicPagesBefore);
    openAddPageMenu(e);
  };

  const handleAddPageOfType = (pageType: PageTypes, questionType?: QuestionTypes) => {
    // Snapshot BEFORE adding — the dialog's Cancel will revert to this state,
    // removing the newly created page + sub-entity if the user discards.
    const preAddSnapshot = cloneDeep(getValues());
    const uuid = addPageAt(pageType, insertAtIndex, questionType);
    // Auto-open the edit dialog for the newly created page if it's editable —
    // its own Apply/Cancel commits or fully reverts the add, so no separate
    // version is pushed here in that case. Pages without a form (none today,
    // but the registry allows it) have no other commit point, so push now.
    if (isQuizPageEditable(pageType)) {
      setSelectedPage({ page: pageType, pageRef: uuid, preAddSnapshot });
      toggle();
    } else {
      commitVersion(`Added ${getQuizPageLabel(pageType)} page`);
    }
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteUuid) return;
    deletePage(pendingDeleteUuid);
    commitVersion('Deleted page');
    // ConfirmationDialog calls onClose right after onConfirm — that resets
    // pendingDeleteUuid to null via setPendingDeleteUuid(null) below.
  }

  return (
    <Fragment>
      {open && selectedPage ? (
        <QuizPageEditForm
          open={open}
          onClose={handleClosePageEdit}
          page={selectedPage.page}
          pageRef={selectedPage.pageRef}
          quizId={quizId}
          preAddSnapshot={selectedPage.preAddSnapshot}
        />
      ) : null}
      {pendingDeleteUuid ? (
        <ConfirmationDialog
          open={!!pendingDeleteUuid}
          loading={false}
          title={t('dialogs.quizAgent.deletePageTitle')}
          content={t('dialogs.quizAgent.deletePageContent')}
          onClose={() => setPendingDeleteUuid(null)}
          onConfirm={handleConfirmDelete}
        />
      ) : null}
      <AddPageMenu>
        {![QuizTypes.Survey, QuizTypes.TeamSelector].includes(quiz.type) ? (
          <AddPageMenuItem onClick={() => handleAddPageOfType(PageTypes.Question)}>
            {t('labels.question')}
          </AddPageMenuItem>
        ) : null}
        <AddPageMenuItem onClick={() => handleAddPageOfType(PageTypes.AdditionalQuestions)}>
          {t('labels.additionalQuestion')}
        </AddPageMenuItem>
        <AddPageMenuItem onClick={() => handleAddPageOfType(PageTypes.Banner)}>
          {t('labels.interstitial')}
        </AddPageMenuItem>
        {eventsFeatureEnable && quizId ? (
          <AddPageMenuItem onClick={() => handleAddPageOfType(PageTypes.Waiting)}>
            {t('labels.waiting')}
          </AddPageMenuItem>
        ) : null}
        {quiz.type !== QuizTypes.Survey ? (
          <AddPageMenuItem onClick={() => handleAddPageOfType(PageTypes.Timer)}>
            {t('labels.timer')}
          </AddPageMenuItem>
        ) : null}
      </AddPageMenu>
      <div className="flex w-full h-full">
        <div className="h-full border-r border-gray-400 flex flex-col flex-shrink-0" style={{ backgroundColor: '#383737', width: 480 }}>
          <div className="border-b border-gray-400 p-4">
            <p className="text-xl font-bold text-white">{quiz.quizName}</p>
            <p className="font-semibold text-white" style={{ color: 'rgba(255,255,255,0.6)' }}>{quizTypesLabels[quiz.type as QuizTypes]}</p>
          </div>
          <MessagesList />
          <div className="relative border-t border-gray-400 p-4">
            {pageContext ? (
              <div
                className="inline-flex items-center gap-1.5 max-w-full mb-2 px-2 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
              >
                <BsStars fontSize="12px" />
                <span className="truncate">{pageContext.reference}</span>
                <button
                  type="button"
                  onClick={() => setPageContext(null)}
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ opacity: 0.7 }}
                >
                  <MdClose fontSize="14px" />
                </button>
              </div>
            ) : null}
            <Textarea
              ref={textAreaRef}
              id="agent-response" name="agent-response"
              placeholder={
                pageContext && PAGE_FIELD_HINTS[pageContext.page]
                  ? `${t('form.placeholder.askForChanges')}, e.g. update ${PAGE_FIELD_HINTS[pageContext.page]}`
                  : t('form.placeholder.askForChanges')
              }
              value={message}
              onChange={onChange}
              onKeyPress={onKeyPress}
              rows={6}
              // disabled={loading}
            />
            <IconButton
              onClick={handleSend}
              disabled={!message.trim() || loading}
              className="absolute bottom-12 right-6 bg-primary hover:bg-primary"
              size="sm"
            >
              <FaArrowUp />
            </IconButton>
          </div>
        </div>
        <div className="flex-grow h-full flex items-center px-4" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
          {(() => {
            const quizNameSlug = quiz?.quizNameSlug || `${kebabCase(quiz.quizName)}`;
            const isGenerating = !quizNameSlug;
            const hasNoPages = !screens.some(s => !!s.pageRef);
            // Find the last pre-game screen index: stop at the first post-game
            // indicator or at a post-game 'register' (when !showPriorQuiz).
            const postGamePages = new Set(['post-quiz', 'results', 'leaderboard']);
            let emptyPagesGapAfterIndex = -1;
            if (hasNoPages) {
              for (let i = 0; i < screens.length; i++) {
                const pg = screens[i].page;
                if (postGamePages.has(pg)) break;
                if (pg === 'register' && !quiz.registrationPage?.showPriorQuiz) break;
                emptyPagesGapAfterIndex = i;
              }
            }
            return (
            <Fragment>
              <PromoVideoTile
                promoVideo={quiz.promoVideo}
                isGenerating={isGenerating}
                loading={loading}
              />
              {screens.map((screen, index) => {
            const editable = isQuizPageEditable(screen.page);
            const isLastScreen = index === screens.length - 1;
            const nextScreen = screens[index + 1];
            const isEmptyPagesGap = index === emptyPagesGapAfterIndex;
            const showGap = !isLastScreen && (!!screen.pageRef || !!nextScreen?.pageRef || isEmptyPagesGap);
            const isGapHovered = hoveredGap === index;
            const previewPage = screen.page === PageTypes.Banner ? 'media' : screen.page;
            return (
            <Fragment key={`quiz-agent-preview-${screen.page}-${index}`}>
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center gap-2 h-8">
                  <p className="text-sm font-medium text-center capitalize" style={{ color: 'rgba(255,255,255,0.6)' }}>{screen.label}</p>
                  {!isGenerating && (
                    <div className="flex items-center">
                      <Tooltip title={t('dialogs.quizAgent.aiEditTooltip')}>
                        <IconButton onClick={() => handleAiEdit(screen)} size="sm" disabled={loading}>
                          <BsStars />
                        </IconButton>
                      </Tooltip>
                      {editable && (
                        <Tooltip title={t('buttons.edit')}>
                          <IconButton onClick={() => handleOpenPageEdit(screen.page, screen.pageRef)} size="sm" disabled={loading}>
                            <FaEdit />
                          </IconButton>
                        </Tooltip>
                      )}
                      {screen.pageRef && (
                        <Tooltip title={t('buttons.remove')}>
                          <IconButton onClick={() => setPendingDeleteUuid(screen.pageRef!)} size="sm" disabled={loading} className="hover:text-red-500 transition ease-in-out">
                            <MdDeleteOutline />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
                <div
                  className="
                    flex flex-col justify-center items-center
                    relative my-4 mx-auto flex-shrink-0
                  "
                  style={{
                    width: '380px',
                    height: '80vh',
                  }}
                >
                  {isGenerating && (
                    <div
                      className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center gap-4 p-4 border border-dashed border-gray-600 rounded-xl"
                      style={{ pointerEvents: 'none' }}
                    >
                      <FiLayout size={48} color="rgba(255,255,255,0.6)" />
                      <p className="text-center text-gray-600 px-4">Generating</p>
                    </div>
                  )}
                  {quizHash ? (
                    <div
                      style={{ width: '100%', height: '100%', borderRadius: '16px', overflow: 'hidden' }}
                      onWheel={(e) => {
                        const iframe = document.getElementById(`quiz-agent-preview-${screen.page}-${index}`) as HTMLIFrameElement | null;
                        iframe?.contentWindow?.postMessage({ action: 'scroll', deltaY: e.deltaY }, '*');
                      }}
                    >
                      <QuizFrame
                        style={{
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                        }}
                        id={`quiz-agent-preview-${screen.page}-${index}`}
                        quizNameSlug={quizNameSlug}
                        quizHash={quizHash}
                        pageHash={getPageHash(screen)}
                        preview="quiz"
                        page={previewPage}
                        pageRef={screen.pageRef}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              {!isLastScreen && (
                <div
                  className="flex-shrink-0 flex items-start justify-center"
                  style={{ width: 32, height: '80vh', paddingTop: 48 }}
                  onMouseEnter={() => showGap && !isGenerating && setHoveredGap(index)}
                  onMouseLeave={() => showGap && !isGenerating && setHoveredGap(null)}
                >
                  {showGap && (
                    <Tooltip title={t('buttons.addPage', 'Add page')}>
                      <button
                        onClick={(e) => handleOpenAddPageMenu(e, index)}
                        disabled={isGenerating || loading}
                        style={getAddButtonStyle(isGapHovered, isGenerating || loading)}
                      >
                        <FaPlus size={isGapHovered ? 14 : 11} />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
            </Fragment>
            );
          })}
            </Fragment>
            );
          })()}
        </div>
      </div>
    </Fragment>
  )
};

export default QuizAgentChatScreen;
